/**
 * FAMILY PRODUCT GRID - v5.0 (design fixes T8 + T11, Wes 2026-10-01)
 *
 * v5.0: neutral colours + brand-red Add buttons (CSS), one Thread column with the full thread size
 * (custom.thread_size), "Thread coverage" instead of the duplicate Thread column, a $/pc column,
 * auto-hidden empty / single-value columns, pre-filter to the current thread size + finish with
 * removable pills, 25 rows in the DOM + "Show 25 more", smaller pack first within a length,
 * mobile (<=749px) card rows, current variant row highlighted (follows the PDP pack choice).
 *
 * v5.1 unit price (Spock D1-D3, 2026-10-01): pieces = variant metafield custom.pieces_per_pack when present, else the
 * "(N pcs)" in the variant title. Whether a row shows $/pc, $/ft (threaded rod) or nothing is decided by
 * window.EcomPPP (assets/ecom-ppp.js + data lists in assets/ecom-ppp-data.js). No rules loaded -> no unit price.
 * Product metafield custom.pack_qty is still a cross-check on the Package row (mismatch -> hidden for that product).
 *
 * v4.0 (2026-06-18): data is fetched client-side from /collections/all/family-{code}?view=family-json.
 */

(function() {
  'use strict';

  const dataScript = document.getElementById('family-products-data');
  if (!dataScript) {
    console.warn('[FamilyGrid] No config data found');
    return;
  }

  let familyData;
  try {
    familyData = JSON.parse(dataScript.textContent);
  } catch (e) {
    console.error('[FamilyGrid] Failed to parse config data:', e);
    return;
  }

  const config = familyData.config;
  let allProducts = [];

  const wrapper = document.querySelector('.family-grid-wrapper');
  const grid = document.getElementById('family-grid-table');
  const tbody = document.getElementById('family-grid-body');
  const filtersContainer = document.getElementById('family-filters');
  const pillsContainer = document.getElementById('family-active-filters');
  const moreWrap = document.getElementById('family-grid-more');
  const itemsSelectedLabel = document.querySelector('.items-selected');
  const bulkAddBtn = document.getElementById('bulk-add-cart');

  if (!grid || !tbody) return;

  const PAGE_SIZE = 25;
  const ColumnConfig = window.FamilyGridColumns || { default: [], getColumnsForType: () => [] };
  const FS = window.FastenerSort || null;
  let columns = ColumnConfig.default;
  let visibleColumns = columns;

  let currentSort = { field: 'threadDisplay', direction: 'asc' };
  let activeFilters = {};
  let visibleCount = PAGE_SIZE;
  let lastResult = [];

  function currentVariantId() {
    const input = document.getElementById('variant-id');
    return String((input && input.value) || config.currentVariantId || '');
  }

  // =============================================
  // FETCH PRODUCT DATA
  // =============================================

  function showError(message) {
    tbody.innerHTML = `
      <tr class="no-results">
        <td colspan="12" class="family-grid-message">${message}</td>
      </tr>
    `;
  }

  async function fetchAllPages(baseUrl) {
    const products = [];
    let page = 1;
    let totalPages = 1;
    do {
      const url = page === 1 ? baseUrl : `${baseUrl}&page=${page}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Fetch failed with status ${res.status}`);
      const data = await res.json();
      products.push(...(data.products || []));
      totalPages = data.totalPages || 1;
      page++;
    } while (page <= totalPages);
    return products;
  }

  // ---- pieces + $/pc (brief §4.1) ----
  function parsePieces(str) {
    const m = String(str || '').match(/\(([\d,]+)\s*pcs?\)/i);
    if (!m) return 0;
    const n = parseInt(m[1].replace(/,/g, ''), 10);
    return n > 0 ? n : 0;
  }
  function formatPPP(priceCents, pieces) {
    if (!pieces || pieces <= 0 || !(priceCents >= 0)) return '';
    const mills = Math.floor((priceCents * 20 + pieces) / (pieces * 2));
    if (mills < 1000) return '$0.' + String(mills % 1000).padStart(3, '0');
    const cents = Math.floor((priceCents * 2 + pieces) / (pieces * 2));
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function enrich(products) {
    const badProducts = new Set();
    products.forEach(p => {
      p.isCurrent = p.handle === config.currentProductHandle;
      p.threadDisplay = p.threadSize || p.diameter || null;
      p.priceCents = (typeof p.priceCents === 'number') ? p.priceCents : Math.round(parseFloat(p.price || 0) * 100);
      p.pieces = (parseInt(p.ppn, 10) > 0) ? parseInt(p.ppn, 10) : parsePieces(p.packQty);
      const meta = parseInt(p.packQtyMeta, 10) || 0;
      if (meta > 0 && p.pieces > 0 && /^package/i.test(String(p.packQty || '')) && p.pieces !== meta) {
        badProducts.add(p.id);
        console.warn('[FamilyGrid] $/pc hidden for', p.handle, '- variant says', p.pieces, 'pcs, custom.pack_qty =', meta, '(Spock D1)');
      }
    });
    products.forEach(p => {
      if (badProducts.has(p.id)) p.pieces = 0;
      const d = (!badProducts.has(p.id) && window.EcomPPP) ? window.EcomPPP.decide({
        productId: p.id, variantId: p.variantId, priceCents: p.priceCents, label: p.packQty,
        piecesMf: p.ppn, ruleV: p.pppRuleV, ruleP: p.pppRuleP, stickFtMf: p.stickFt
      }) : null;
      const show = d && d.mode !== 'hide';
      p.pppUnit = show ? d.unit : null;
      p.pppText = show ? (d.unit === 'pc' ? d.text : d.text + '/' + d.unit) : null;
      p.pppSort = show ? d.perUnit : null;
      p.pppReason = d ? d.reason : (badProducts.has(p.id) ? 'pack_qty_mismatch' : 'rules_unavailable');
      p.packLabel = String(p.packQty || '').replace(/\s*\(([^)]*)\)\s*$/, (m, inner) => ' · ' + inner);
    });
    return products;
  }

  async function loadFamilyProducts() {
    if (!config.familyFetchUrl) {
      console.warn('[FamilyGrid] No fetch URL in config');
      showError('Unable to load family products.');
      return;
    }
    try {
      const fetched = await fetchAllPages(config.familyFetchUrl);
      if (!window.EcomPPP && document.readyState === 'loading') await new Promise(r => document.addEventListener('DOMContentLoaded', r, { once: true }));
      allProducts = enrich(fetched);
      console.log('[FamilyGrid] Loaded', allProducts.length, 'product rows from', config.familyFetchUrl);
      if (allProducts.length === 0) {
        showError('No products found for this family.');
        return;
      }
      setupColumns();
      applyDefaultFilters();
      renderFilterControls();
      renderTable();
    } catch (err) {
      console.error('[FamilyGrid] Failed to load family products:', err);
      showError('Failed to load family products. Please refresh the page.');
    }
  }

  // =============================================
  // COLUMNS
  // =============================================

  const isEmpty = v => v === null || v === undefined || v === '' || v === '-';

  function normalizeColumns(cols) {
    const out = [];
    cols.forEach(col => {
      let c = Object.assign({}, col);
      if (c.key === 'diameter' && (c.label === 'Thread' || c.label === 'Size')) {
        c.key = 'threadDisplay'; c.label = 'Thread';
      } else if (c.key === 'threadStyle') {
        c = { key: 'threadCoverage', label: 'Thread coverage', sortable: false, filterable: false };
      } else if (c.key === 'id') {
        c.key = 'insideDiameter';           // was the product ID (bug)
      } else if (c.key === 'packQty') {
        c.key = 'packLabel'; c.align = null;
      }
      out.push(c);
      if (c.key === 'price') {
        out.push({ key: 'pppText', sortKey: 'pppSort', label: '$/pc', sortable: true, filterable: false, align: 'right', cls: 'col-ppp' });
      }
    });
    return out;
  }

  function setupColumns() {
    const productType = config.familyName || config.productType || '';
    const hideFinish = ColumnConfig.shouldHideFinish ? ColumnConfig.shouldHideFinish(allProducts) : false;
    let cols = ColumnConfig.getColumnsForType ? ColumnConfig.getColumnsForType(productType, { hideFinish }) : ColumnConfig.default;
    if (!cols || !cols.length) cols = ColumnConfig.default;
    if (!cols.some(c => c.key === 'price')) cols = cols.concat([{ key: 'price', label: 'Price', sortable: true, align: 'right', format: 'money' }]);
    cols = normalizeColumns(cols);
    // Columns that are empty for the whole family, or (material / grade / coverage) constant across it, are dropped.
    const singleValueHide = ['material', 'grade', 'threadCoverage'];
    columns = cols.filter(col => {
      const vals = new Set(allProducts.map(p => p[col.key]).filter(v => !isEmpty(v)));
      if (col.key === 'pppText') {
        const units = new Set(allProducts.map(p => p.pppUnit).filter(Boolean));
        col.label = (units.size === 1 && units.has('ft')) ? '$/ft' : '$/pc';
        return vals.size > 0;
      }
      if (vals.size === 0) return false;
      if (singleValueHide.includes(col.key) && vals.size === 1) return false;
      return true;
    });
    if (!columns.some(c => c.key === 'threadDisplay') && columns.length) currentSort.field = columns[0].sortKey || columns[0].key;
    console.log('[FamilyGrid] Product type:', productType, '| Hide finish:', hideFinish, '| Columns:', columns.map(c => c.key).join(','));
  }

  // Pre-filter to the current product's thread size + finish (only when the family has more than one).
  function applyDefaultFilters() {
    const cur = allProducts.find(p => p.isCurrent);
    if (!cur) return;
    ['threadDisplay', 'finish'].forEach(key => {
      if (isEmpty(cur[key])) return;
      const vals = new Set(allProducts.map(p => p[key]).filter(v => !isEmpty(v)));
      if (vals.size > 1) activeFilters[key] = String(cur[key]);
    });
  }

  function labelFor(key) {
    const c = columns.find(c => c.key === key);
    if (c) return c.label;
    return { threadDisplay: 'Thread', finish: 'Finish', material: 'Material', grade: 'Grade', length: 'Length' }[key] || key;
  }

  // =============================================
  // RENDER
  // =============================================

  function renderTable() {
    let products = applySort(applyFilters(allProducts.filter(p => p !== null)));
    lastResult = products;

    // Auto-hide any column that is empty for every row in the current result
    visibleColumns = columns.filter(col => products.some(p => !isEmpty(p[col.key])));
    renderTableHeader();
    renderPills();

    tbody.innerHTML = '';
    const colCount = visibleColumns.length + 3;
    if (products.length === 0) {
      tbody.innerHTML = `
        <tr class="no-results">
          <td colspan="${colCount}" class="family-grid-message">
            No products match your filters. <button type="button" class="family-link-btn" data-clear-filters>Show all sizes</button>
          </td>
        </tr>`;
      renderMore(0);
      return;
    }
    const frag = document.createDocumentFragment();
    products.slice(0, visibleCount).forEach(p => frag.appendChild(createProductRow(p)));
    tbody.appendChild(frag);
    renderMore(products.length);
    updateFilterOptions();
    updateBulkSelectionState();
  }

  function renderMore(total) {
    if (!moreWrap) return;
    const shown = Math.min(visibleCount, total);
    if (total <= PAGE_SIZE && shown >= total) { moreWrap.innerHTML = total ? `<span class="family-grid-status" role="status">Showing ${total} of ${total}</span>` : ''; return; }
    moreWrap.innerHTML =
      `<span class="family-grid-status" role="status">Showing ${shown} of ${total}</span>` +
      (shown < total ? `<button type="button" class="family-more-btn" id="family-more-btn">Show ${Math.min(PAGE_SIZE, total - shown)} more</button>` : '');
    const btn = document.getElementById('family-more-btn');
    if (btn) btn.addEventListener('click', () => {
      const start = visibleCount;
      visibleCount += PAGE_SIZE;
      const frag = document.createDocumentFragment();
      lastResult.slice(start, visibleCount).forEach(p => frag.appendChild(createProductRow(p)));
      tbody.appendChild(frag);
      renderMore(lastResult.length);
      const next = tbody.children[start] && tbody.children[start].querySelector('a, input, button');
      if (next) next.focus({ preventScroll: true });
    });
  }

  function renderTableHeader() {
    const thead = grid.querySelector('thead');
    if (!thead) return;
    let headerHtml = '<tr>';
    headerHtml += '<th class="col-select"><input type="checkbox" id="select-all-checkbox" aria-label="Select all"></th>';
    visibleColumns.forEach(col => {
      const sortField = col.sortKey || col.key;
      const sortClass = col.sortable ? 'sortable' : '';
      const sorted = col.sortable && currentSort.field === sortField ? ` sorted-${currentSort.direction}` : '';
      const alignClass = col.align ? `col-${col.align}` : '';
      const sortIcon = col.sortable ? '<span class="sort-icon"></span>' : '';
      const aria = col.sortable ? ` aria-sort="${currentSort.field === sortField ? (currentSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}" tabindex="0"` : '';
      headerHtml += `<th class="${sortClass}${sorted} ${alignClass} ${col.cls || ''}" data-sort="${sortField}"${aria}>${col.label}${sortIcon}</th>`;
    });
    headerHtml += '<th class="col-qty">Qty</th>';
    headerHtml += '<th class="col-cart"><span class="visually-hidden">Add to cart</span></th>';
    headerHtml += '<th class="col-card" aria-hidden="true"></th>';
    headerHtml += '</tr>';
    thead.innerHTML = headerHtml;

    const selectAllBox = document.getElementById('select-all-checkbox');
    if (selectAllBox) {
      selectAllBox.addEventListener('change', () => {
        tbody.querySelectorAll('.row-select-checkbox:not(:disabled)').forEach(cb => { cb.checked = selectAllBox.checked; });
        updateBulkSelectionState();
      });
    }
    thead.querySelectorAll('th.sortable').forEach(th => {
      const go = () => {
        const field = th.dataset.sort;
        if (currentSort.field === field) currentSort.direction = currentSort.direction === 'asc' ? 'desc' : 'asc';
        else currentSort = { field, direction: 'asc' };
        visibleCount = PAGE_SIZE;
        renderTable();
      };
      th.addEventListener('click', go);
      th.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } });
    });
  }

  function renderFilterControls() {
    if (!filtersContainer) return;
    const filterableCols = columns.filter(c => c.filterable);
    let html = '';
    filterableCols.forEach(col => {
      html += `
        <div class="filter-group">
          <label for="filter-${col.key}">${col.label}</label>
          <select id="filter-${col.key}" data-filter="${col.key}">
            <option value="">All</option>
          </select>
        </div>`;
    });
    html += '<button type="button" class="filter-clear" id="clear-filters" data-clear-filters>Show all sizes</button>';
    filtersContainer.innerHTML = html;
    filtersContainer.dataset.initialized = 'true';

    filtersContainer.querySelectorAll('select').forEach(select => {
      select.addEventListener('change', (e) => {
        const field = e.target.dataset.filter;
        if (e.target.value) activeFilters[field] = e.target.value; else delete activeFilters[field];
        visibleCount = PAGE_SIZE;
        renderTable();
      });
    });
    updateFilterOptions();
    updateFilterToggle();
  }

  function renderPills() {
    if (!pillsContainer) return;
    const entries = Object.entries(activeFilters).filter(([, v]) => v);
    pillsContainer.innerHTML = entries.map(([k, v]) =>
      `<button type="button" class="family-pill" data-remove-filter="${escapeHtml(k)}" aria-label="Remove filter ${escapeHtml(labelFor(k))}: ${escapeHtml(v)}">${escapeHtml(labelFor(k))}: ${escapeHtml(v)} <span aria-hidden="true">×</span></button>`
    ).join('') + (entries.length ? '<button type="button" class="family-link-btn" data-clear-filters>Show all sizes</button>' : '');
    updateFilterToggle();
  }

  function updateFilterToggle() {
    const t = document.getElementById('family-filter-toggle');
    if (!t) return;
    const n = Object.values(activeFilters).filter(Boolean).length;
    t.textContent = `Filter sizes${n ? ` (${n} active)` : ''}`;
  }

  function createProductRow(product) {
    const tr = document.createElement('tr');
    tr.dataset.productId = product.id;
    tr.dataset.variantId = product.variantId;
    if (product.isCurrent) tr.classList.add('current-product');
    if (String(product.variantId) === currentVariantId()) tr.classList.add('current-variant');

    let rowHtml = `
      <td class="col-select">
        <input type="checkbox" class="row-select-checkbox" data-variant-id="${product.variantId}" aria-label="Select ${escapeHtml(product.title || '')} ${escapeHtml(product.packQty || '')}" ${!product.available ? 'disabled' : ''}>
      </td>`;

    visibleColumns.forEach((col, idx) => {
      let value = product[col.key];
      const cellClass = `${col.align ? `col-${col.align}` : ''} ${col.cls || ''} col-k-${col.key}`;
      if (col.format === 'money' && !isEmpty(value)) value = formatMoney(parseFloat(value));
      else if (isEmpty(value)) value = '-';
      else value = escapeHtml(String(value));
      if (idx === 0) rowHtml += `<td class="${cellClass}"><a href="/products/${product.handle}" class="product-link">${value}</a></td>`;
      else rowHtml += `<td class="${cellClass}">${value}</td>`;
    });

    const label = 'Quantity for ' + escapeHtml((product.threadDisplay || '') + ' × ' + (product.length || '') + ', ' + (product.packQty || ''));
    rowHtml += `
      <td class="col-qty">
        <input type="number" class="qty-input" value="1" min="1" max="9999" inputmode="numeric" aria-label="${label}" data-variant-id="${product.variantId}" ${!product.available ? 'disabled' : ''}>
      </td>
      <td class="col-cart">
        <button type="button" class="btn-add-row" data-variant-id="${product.variantId}" aria-label="Add ${escapeHtml(product.packQty || '')} of ${escapeHtml(product.title || '')} to cart" ${!product.available ? 'disabled' : ''}>
          ${product.available ? 'Add' : 'Out'}
        </button>
      </td>`;

    // Mobile card summary (<=749px; hidden on desktop)
    const size = [product.threadDisplay, product.length].filter(v => !isEmpty(v)).map(escapeHtml).join(' × ');
    const priceLine = formatMoney(parseFloat(product.price)) + (product.pppText ? ` · <strong>${product.pppUnit === 'pc' ? product.pppText + '/pc' : product.pppText}</strong>` : '');
    rowHtml += `
      <td class="col-card">
        <a href="/products/${product.handle}" class="family-card__size">${size || escapeHtml(product.title || '')}</a>
        <span class="family-card__pack">${escapeHtml(product.packLabel || '')}</span>
        <span class="family-card__price">${priceLine}</span>
      </td>`;

    tr.innerHTML = rowHtml;
    return tr;
  }

  // =============================================
  // FILTERING
  // =============================================

  function applyFilters(products) {
    return products.filter(p => {
      for (const [field, value] of Object.entries(activeFilters)) {
        if (value && String(p[field]) !== String(value)) return false;
      }
      return true;
    });
  }

  function updateFilterOptions() {
    columns.filter(c => c.filterable).forEach(col => {
      const select = filtersContainer && filtersContainer.querySelector(`[data-filter="${col.key}"]`);
      if (!select) return;
      const values = [...new Set(allProducts.map(p => p[col.key]).filter(v => !isEmpty(v)))];
      values.sort((a, b) => compareField(col.key, a, b));
      const currentValue = activeFilters[col.key] || '';
      select.innerHTML = '<option value="">All</option>';
      values.forEach(v => {
        const opt = document.createElement('option');
        opt.value = v; opt.textContent = v;
        if (String(v) === String(currentValue)) opt.selected = true;
        select.appendChild(opt);
      });
    });
  }

  function clearFilters() {
    activeFilters = {};
    if (filtersContainer) filtersContainer.querySelectorAll('select').forEach(s => s.value = '');
    visibleCount = PAGE_SIZE;
    renderTable();
  }

  // =============================================
  // SORTING: thread, then length, then smaller pack first (Package before Bulk Box)
  // =============================================

  function compareField(field, a, b) {
    if (isEmpty(a) && isEmpty(b)) return 0;
    if (isEmpty(a)) return 1;
    if (isEmpty(b)) return -1;
    if (FS && (field === 'threadDisplay' || field === 'diameter')) return FS.compareThreadSize(a, b) || String(a).localeCompare(String(b), undefined, { numeric: true });
    if (FS && field === 'length') return FS.compareLength(a, b) || String(a).localeCompare(String(b), undefined, { numeric: true });
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return compareValues(a, b);
  }

  function applySort(products) {
    const { field, direction } = currentSort;
    const dir = direction === 'asc' ? 1 : -1;
    return products.slice().sort((a, b) => {
      let r = compareField(field, a[field], b[field]) * dir;
      if (r === 0 && field !== 'threadDisplay') r = compareField('threadDisplay', a.threadDisplay, b.threadDisplay);
      if (r === 0 && field !== 'length') r = compareField('length', a.length, b.length);
      if (r === 0) r = (a.id || 0) - (b.id || 0);                       // keep a product's packs together
      if (r === 0) r = (a.pieces || Infinity) - (b.pieces || Infinity); // smaller pack first
      return r;
    });
  }

  function compareValues(valA, valB) {
    const numA = parseFraction(valA);
    const numB = parseFraction(valB);
    if (numA !== null && numB !== null) return numA - numB;
    return String(valA).localeCompare(String(valB), undefined, { numeric: true });
  }

  function parseFraction(str) {
    if (str == null) return null;
    str = String(str).trim();
    if (str.startsWith('#')) {
      const num = parseFloat(str.slice(1));
      return isNaN(num) ? null : num - 100;
    }
    const fractionMatch = str.match(/^(\d+)?[-\s]?(\d+)\/(\d+)/);
    if (fractionMatch) {
      const whole = fractionMatch[1] ? parseInt(fractionMatch[1]) : 0;
      return whole + (parseInt(fractionMatch[2]) / parseInt(fractionMatch[3]));
    }
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  }

  // =============================================
  // BULK SELECT + BULK ADD TO CART
  // =============================================

  function updateBulkSelectionState() {
    const checkedBoxes = tbody.querySelectorAll('.row-select-checkbox:checked');
    const count = checkedBoxes.length;

    if (itemsSelectedLabel) {
      itemsSelectedLabel.textContent = `${count} item${count === 1 ? '' : 's'} selected`;
    }
    if (bulkAddBtn) {
      bulkAddBtn.disabled = count === 0;
    }

    // Keep select-all checkbox in sync (checked / indeterminate / unchecked)
    const selectAllBox = document.getElementById('select-all-checkbox');
    if (selectAllBox) {
      const allBoxes = tbody.querySelectorAll('.row-select-checkbox:not(:disabled)');
      selectAllBox.checked = allBoxes.length > 0 && count === allBoxes.length;
      selectAllBox.indeterminate = count > 0 && count < allBoxes.length;
    }
  }

  function handleRowCheckboxChange(e) {
    if (!e.target.classList.contains('row-select-checkbox')) return;
    updateBulkSelectionState();
  }

  async function handleBulkAddToCart() {
    const checkedBoxes = Array.from(tbody.querySelectorAll('.row-select-checkbox:checked'));
    if (checkedBoxes.length === 0) return;

    const items = checkedBoxes.map(cb => {
      const row = cb.closest('tr');
      const qtyInput = row.querySelector('.qty-input');
      const qty = parseInt(qtyInput?.value) || 1;
      return { id: parseInt(cb.dataset.variantId), quantity: qty };
    });

    const originalText = bulkAddBtn.textContent;
    bulkAddBtn.disabled = true;
    bulkAddBtn.textContent = 'Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        throw new Error(`Bulk add failed with status ${res.status}`);
      }

      bulkAddBtn.textContent = 'Added!';
      document.dispatchEvent(new CustomEvent('ecom:cart-updated'));
      updateCartCount();

      // Clear selections after a successful add
      checkedBoxes.forEach(cb => { cb.checked = false; });
      updateBulkSelectionState();

      setTimeout(() => {
        bulkAddBtn.textContent = originalText;
      }, 2000);
    } catch (err) {
      console.error('[FamilyGrid] Bulk add to cart failed:', err);
      bulkAddBtn.textContent = 'Error — try again';
      setTimeout(() => {
        bulkAddBtn.textContent = originalText;
        bulkAddBtn.disabled = checkedBoxes.length === 0;
      }, 2500);
    }
  }

  // =============================================
  // ADD TO CART
  // =============================================

  function handleAddToCart(e) {
    if (!e.target.classList.contains('btn-add-row')) return;
    
    const btn = e.target;
    const variantId = btn.dataset.variantId;
    const row = btn.closest('tr');
    const qtyInput = row.querySelector('.qty-input');
    const qty = parseInt(qtyInput?.value) || 1;
    
    btn.classList.add('adding');
    btn.textContent = '...';
    
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId), quantity: qty }]
      })
    })
    .then(res => {
      if (res.ok) {
        btn.classList.remove('adding');
        btn.classList.add('added');
        btn.textContent = '✓';
        
        // Update cart count in header if possible
        updateCartCount();
        
        setTimeout(() => {
          btn.classList.remove('added');
          btn.textContent = 'Add';
        }, 2000);
      } else {
        throw new Error('Failed to add');
      }
    })
    .catch(err => {
      btn.classList.remove('adding');
      btn.textContent = 'Error';
      setTimeout(() => { btn.textContent = 'Add'; }, 2000);
    });
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const countEls = document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble');
        countEls.forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) el.classList.remove('hidden');
        });
      })
      .catch(() => {});
  }

  // =============================================
  // HELPERS
  // =============================================

  function formatMoney(cents) {
    // Format as USD - adjust based on shop.money_format if needed
    const dollars = (cents || 0);
    return '$' + dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function escapeHtml(str) {
    if (str == null) return '';
    const div = document.createElement('div');
    div.textContent = str;
    // QA2: also escape quotes, so titles with inch marks (3/8") can't break out of aria-label="..." attributes
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // =============================================
  // INIT
  // =============================================

  tbody.addEventListener('click', handleAddToCart);
  tbody.addEventListener('change', handleRowCheckboxChange);
  if (bulkAddBtn) bulkAddBtn.addEventListener('click', handleBulkAddToCart);
  if (wrapper) wrapper.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-remove-filter]');
    if (rm) {
      const k = rm.getAttribute('data-remove-filter');
      delete activeFilters[k];
      const sel = filtersContainer && filtersContainer.querySelector(`[data-filter="${k}"]`);
      if (sel) sel.value = '';
      visibleCount = PAGE_SIZE;
      renderTable();
      return;
    }
    if (e.target.closest('[data-clear-filters]')) { clearFilters(); return; }
  });
  const toggle = document.getElementById('family-filter-toggle');
  if (toggle && filtersContainer) toggle.addEventListener('click', () => {
    const open = !filtersContainer.classList.contains('is-open');
    filtersContainer.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
  // Follow the PDP pack choice (T8): highlight the current variant row
  document.addEventListener('ecom:variant-change', (e) => {
    const id = String(e.detail && e.detail.variant ? e.detail.variant.id : '');
    tbody.querySelectorAll('tr.current-variant').forEach(tr => tr.classList.remove('current-variant'));
    const row = id && tbody.querySelector(`tr[data-variant-id="${id}"]`);
    if (row) row.classList.add('current-variant');
  });

  loadFamilyProducts();

})();
